import i18next from '@/lib/i18n';
import { supportedLanguages } from '@/i18n/locales';
import type { FieldError } from '@/lib/excel';
import type { DraftRowShape, RowValidator } from './draftRow';
import { normalizedKey } from './normalizedKey';

/**
 * Natural-key spec of a draft row, shared by the Weiter duplicate pass
 * (markDuplicateRows) and the upload merge (upsertUploadedRows) so both agree
 * on row identity.
 */
export interface DuplicateCheck<TData> {
  /** Identity field — also the cell highlighted by the Weiter duplicate pass */
  field: keyof TData & string;
  /** Duplicate identity; defaults to the field's own value */
  keyOf?: (data: TData) => string | undefined;
}

/** Case-insensitive natural key of a row under one check; undefined when blank. */
export function duplicateKeyFor<TData>(
  check: DuplicateCheck<TData>,
  data: TData
): string | undefined {
  const raw = check.keyOf ? check.keyOf(data) : (data[check.field] as unknown);
  return (typeof raw === 'string' && normalizedKey(raw)) || undefined;
}

export interface CrossRowCheck<TData> {
  /** Cell that gets the error highlight on offending rows */
  field: keyof TData & string;
  /** Indices of rows violating the rule, judged across the whole draft */
  findViolations: (rows: readonly TData[]) => Set<number>;
  /** i18n key, translated when the check runs */
  messageKey: string;
}

function applyRowErrors<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  errorsFor: (row: TRow, index: number) => FieldError[]
): { rows: TRow[]; allValid: boolean } {
  const marked = rows.map((row, index) => {
    const errors = errorsFor(row, index);
    if (errors.length === 0) return row;
    return { ...row, isValid: false, errors: [...(row.errors ?? []), ...errors] };
  });
  return { rows: marked, allValid: marked.every((row) => row.isValid) };
}

/**
 * Marks rows flagged by whole-draft rules that aren't duplicate-shaped —
 * circular parent chains, conflicting descriptions for one Stelle, etc.
 */
export function markCrossRowViolations<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  checks: Array<CrossRowCheck<TRow['data']>>
): { rows: TRow[]; allValid: boolean } {
  const violationsPerCheck = checks.map((check) =>
    check.findViolations(rows.map((row) => row.data))
  );

  return applyRowErrors(rows, (_, index) =>
    checks
      .filter((_, checkIndex) => violationsPerCheck[checkIndex].has(index))
      .map((check) => ({ field: check.field, message: i18next.t(check.messageKey) }))
  );
}

/**
 * Weiter-time duplicate pass over a draft table: rows sharing the same
 * (case-insensitive) key are all marked invalid on the offending cell.
 * Complements the per-row schema validation, which cannot see across rows.
 */
export function markDuplicateRows<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  checks: Array<DuplicateCheck<TRow['data']>>
): { rows: TRow[]; allValid: boolean } {
  const isDuplicate = duplicatePredicate(rows, checks);
  return applyRowErrors(rows, (row) =>
    checks
      .filter((check) => isDuplicate(check, row))
      .map((check) => ({ field: check.field, message: i18next.t(DUPLICATE_ENTRY_KEY) }))
  );
}

/**
 * Drops duplicate marks that no longer hold after an edit or delete: the mark
 * is judged across rows, so removing one of two duplicates must clear the
 * other. Never adds a mark — unmarked rows stay neutral until Weiter.
 */
export function clearStaleDuplicateMarks<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  checks: Array<DuplicateCheck<TRow['data']>>
): TRow[] {
  const isDuplicate = duplicatePredicate(rows, checks);
  const marks = checks.map((check) => ({
    check,
    keys: duplicateMarksInEveryLanguage(check).map(markKey)
  }));
  return rows.map((row) => {
    if (row.isValid || !row.errors) return row;
    const staleMarks = new Set(
      marks.filter(({ check }) => !isDuplicate(check, row)).flatMap(({ keys }) => keys)
    );
    const errors = row.errors.filter((error) => !staleMarks.has(markKey(error)));
    if (errors.length === row.errors.length) return row;
    return errors.length > 0 ? { ...row, errors } : { ...row, isValid: true, errors: undefined };
  });
}

const DUPLICATE_ENTRY_KEY = 'bulkOnboarding.validation.duplicateEntry';

const markKey = ({ field, message }: FieldError) => JSON.stringify([field, message]);

// A mark keeps the language it was set in; the UI language can change before
// the edit that makes it stale.
function duplicateMarksInEveryLanguage(check: { field: string }): FieldError[] {
  return supportedLanguages.map((lng) => ({
    field: check.field,
    message: i18next.t(DUPLICATE_ENTRY_KEY, { lng })
  }));
}

function duplicatePredicate<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  checks: Array<DuplicateCheck<TRow['data']>>
): (check: DuplicateCheck<TRow['data']>, row: TRow) => boolean {
  const countsPerCheck = new Map(
    checks.map((check) => {
      const counts = new Map<string, number>();
      for (const row of rows) {
        const key = duplicateKeyFor(check, row.data);
        if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      return [check, counts];
    })
  );
  return (check, row) => {
    const key = duplicateKeyFor(check, row.data);
    return !!key && (countsPerCheck.get(check)?.get(key) ?? 0) > 1;
  };
}

/**
 * The full authoritative Weiter pass: per-row schema validation plus the
 * cross-row duplicate and rule checks, in the right order. Submit handlers
 * call this instead of composing the passes by hand.
 */
export function validateDraftRows<TRow extends DraftRowShape<unknown>>(
  rows: TRow[],
  validateRow: RowValidator<TRow['data']>,
  duplicateChecks: Array<DuplicateCheck<TRow['data']>>,
  crossRowChecks: Array<CrossRowCheck<TRow['data']>> = []
): { rows: TRow[]; allValid: boolean } {
  // Manually added/edited rows stay unvalidated while typing, so every row is re-checked.
  const validated = rows.map((row) => ({ ...row, ...validateRow(row.data) }));
  const deduped = markDuplicateRows(validated, duplicateChecks);
  // Rows the dedupe pass invalidated stay invalid, so its allValid is implied.
  return markCrossRowViolations(deduped.rows, crossRowChecks);
}
